import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import { updateDialog } from '@Containers/Dialogs/DialogsActions';
import { RouteType } from '@Pages/RetroSynthesis/constants';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import { addProjectSuccessAction, clearCreatedProjectState } from '@Pages/RetroSynthesis/RetroSynthesisProjects/actions';
import { fetchData as fetchRouteDataAction, setIsTransferFromReaction } from '@Pages/RetroSynthesis/ProjectRoute/actions';
import { fetchMultiStepReactionPreview as fetchMultiStepReactionPreviewService } from '@Pages/RetroSynthesis/MultiStepReactionDialog/MultiStepReactionService';
import { forceReload as forceReloadThumbnails } from '@Pages/RetroSynthesis/components/RsSlidablePaginated/actions';
import { noCache } from '@Utils/Network';
import namespacedCache from '@Services/NamespacedCache';
import { projectRouteDataCacheKey } from '@Pages/RetroSynthesis/ProjectRoute/constants';
import { projectRoutesCacheKey } from '@Pages/RetroSynthesis/ProjectRoutes/constants';
import { fetchProjectBreadcrumbInfoAction } from '@Pages/RetroSynthesis/RsBreadcrumbs/actions';
import { parseSchemeString, mapRouteLayoutForEnrollingMultiStepReaction } from './utils';
import {
  typeOfMultiStepReactionDialog,
  MULTISTEP_REACTION_DIALOG_FETCH_PREVIEW,
  MULTISTEP_REACTION_DIALOG_FETCH_PREVIEW_SUCCESS,
  MULTISTEP_REACTION_DIALOG_FETCH_PREVIEW_FAILURE,
  MULTISTEP_REACTION_DIALOG_ADD_PROJECT,
  MULTISTEP_REACTION_DIALOG_ADD_PROJECT_SUCCESS,
  MULTISTEP_REACTION_DIALOG_ADD_PROJECT_FAILURE,
  MULTISTEP_REACTION_DIALOG_CLEAR_STATE,
} from './constants';

export const openMultiStepReactionDialog = ({ databaseId, scheme, onClose, rtProjectId, rtRouteId, rtParentId, rtStepId }) => {
  const { rootReactionId, stepReactionIds } = parseSchemeString(scheme);

  return updateDialog({
    id: typeOfMultiStepReactionDialog,
    dialogType: typeOfMultiStepReactionDialog,
    databaseId,
    rootReactionId,
    stepReactionIds,
    onClose,
    singleReaction: false,
    rtProjectId,
    rtRouteId,
    rtParentId,
    rtStepId,
  });
};

export const openMultiStepReactionDialogForSingleReaction = ({ databaseId, reactionId, onClose }) => {
  return updateDialog({
    id: typeOfMultiStepReactionDialog,
    dialogType: typeOfMultiStepReactionDialog,
    databaseId,
    onClose,
    rootReactionId: reactionId,
    stepReactionIds: [reactionId],
    singleReaction: true,
  });
};

export const fetchMultiStepReactionPreview = (params) => {
  return async (dispatch) => {
    dispatch({ type: MULTISTEP_REACTION_DIALOG_FETCH_PREVIEW });

    try {
      const response = await fetchMultiStepReactionPreviewService(params);
      dispatch({
        type: MULTISTEP_REACTION_DIALOG_FETCH_PREVIEW_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: MULTISTEP_REACTION_DIALOG_FETCH_PREVIEW_FAILURE,
        payload: error,
      });
    }
  };
};

export const submitMultiStepReactionProject = withGlobalSpinnerActionEnhancer(
  ({ databaseId, rootReactionId, stepReactionIds, history }) => {
    return async (dispatch) => {
      dispatch({ type: MULTISTEP_REACTION_DIALOG_ADD_PROJECT });

      try {
        const response = await MessageBroker.send(RequestUrls.rsMultiStepAddProject, {
          databaseId,
          rootReactionId,
          stepReactionIds,
        });
        // eslint-disable-next-line no-use-before-define
        handleSubmitReactionResult(dispatch, response, history);
      } catch (error) {
        dispatch({
          type: MULTISTEP_REACTION_DIALOG_ADD_PROJECT_FAILURE,
          payload: error,
        });
      }
    };
  }
);

export const submitSingleStepReactionProject = withGlobalSpinnerActionEnhancer(({ databaseId, reactionId, history }) => {
  return async (dispatch) => {
    dispatch({ type: MULTISTEP_REACTION_DIALOG_ADD_PROJECT });

    try {
      const response = await MessageBroker.send(RequestUrls.rsSingleStepAddProject, {
        databaseId,
        reactionId,
      });
      // eslint-disable-next-line no-use-before-define
      handleSubmitReactionResult(dispatch, response, history);
    } catch (error) {
      dispatch({
        type: MULTISTEP_REACTION_DIALOG_ADD_PROJECT_FAILURE,
        payload: error,
      });
    }
  };
});

export const enrollMultiStepScheme = withGlobalSpinnerActionEnhancer(
  ({ rtProjectId, rtRouteId, rtParentId, rtStepId, routeLayout, history }) => {
    return async (dispatch) => {
      const mappedRouteLayout = mapRouteLayoutForEnrollingMultiStepReaction(routeLayout);
      const addStepsPayload = [
        {
          type: RouteType.PUBLISHED,
          parentId: rtParentId,
          stepId: rtStepId,
          reaction: mappedRouteLayout,
        },
      ];

      dispatch({ type: MULTISTEP_REACTION_DIALOG_ADD_PROJECT });

      try {
        const response = await MessageBroker.send(
          RequestUrls.rsProjectsAddRouteSteps,
          {
            projectId: rtProjectId,
            routeId: rtRouteId,
          },
          noCache(),
          addStepsPayload
        );

        dispatch({
          type: MULTISTEP_REACTION_DIALOG_ADD_PROJECT_SUCCESS,
          payload: response,
        });

        namespacedCache.removeByKey(projectRouteDataCacheKey(rtProjectId, rtRouteId));
        namespacedCache.removeByKey(projectRoutesCacheKey(rtProjectId));

        // Refresh customizedRoutesCount in RsBreadcrumbs
        dispatch(fetchProjectBreadcrumbInfoAction(rtProjectId));

        // Navigate to the updated route
        if (String(response) !== rtRouteId) {
          history.push(RsRoute.getProjectRouteTreeViewLink(rtProjectId, response));
        } else {
          dispatch(fetchRouteDataAction({ projectId: rtProjectId, routeId: rtRouteId, cache: false }));
          history.replace(RsRoute.getProjectRouteTreeViewLink(rtProjectId, response));
        }

        // Refresh thumbnails in RsSlidablePaginated
        dispatch(forceReloadThumbnails(response));
      } catch (error) {
        dispatch({
          type: MULTISTEP_REACTION_DIALOG_ADD_PROJECT_FAILURE,
          payload: error,
        });
      }
    };
  }
);

const handleSubmitReactionResult = (dispatch, response, history) => {
  dispatch({
    type: MULTISTEP_REACTION_DIALOG_ADD_PROJECT_SUCCESS,
    payload: response,
  });
  dispatch(addProjectSuccessAction());
  dispatch(clearCreatedProjectState());
  dispatch(setIsTransferFromReaction(true));
  // Redirect to Tree view
  const { id: projectId } = response;
  const routeId = 1;
  // eslint-disable-next-line no-unused-expressions
  history && history.push(RsRoute.getProjectRouteTreeViewLink(projectId, routeId));
};

export const multiStepReactionDialogClearState = () => ({
  type: MULTISTEP_REACTION_DIALOG_CLEAR_STATE,
});
